import React, {
  FC, useCallback, useLayoutEffect, useMemo, useRef,
} from 'react';
import { Spin } from 'antd';
import type { StepPageProps } from '../../../assessment-portal';
import { useAssessmentPortalState } from '../../../assessment-portal';
import AssessmentQuestionnairePage from '../assessment-questionnaire-page';
import { SubmitErrorAlert } from '../../components/submit-error-alert';
import { AdhdQuestionnaireFlowStep } from '../../data/constants';
import useQuestionnaireSubmission from '../../hooks/use-questionnaire-submission';
import type { SubmissionFailure } from '../../hooks/submission-state';
import {
  loadValidQuestionnaireDraft,
  saveQuestionnaireDraft,
} from '../../utils/questionnaire-draft';
import {
  buildIdentityFromBookingSnapshot,
  hasBookingConfirmationSnapshot,
  loadBookingConfirmationSnapshot,
} from '../../utils/booking-confirmation-snapshot';
import { getQuestionnaireTemplateConfig } from '../../utils/questionnaire-variant';
import type { AdhdQuestionnaireFlowState } from '../../types/flow';

const AssessmentQuestionnaireStep: FC<StepPageProps> = ({ defaultValues, onBack, onNext }) => {
  const {
    flowState,
    updateFlowState,
    goToStep,
  } = useAssessmentPortalState<AdhdQuestionnaireFlowState>();

  const {
    state: submissionState,
    handleSubmitQuestionnaire,
    resetSubmitError,
  } = useQuestionnaireSubmission(flowState);

  const { ageRange } = flowState;
  // Without an age range the adult template is only a placeholder; the effect below redirects.
  const { templateId, templateVersion } = getQuestionnaireTemplateConfig(ageRange ?? 'adult');

  useLayoutEffect(() => {
    if (!ageRange) {
      goToStep(AdhdQuestionnaireFlowStep.AGE_RANGE);
    }
  }, [ageRange, goToStep]);

  const validDraft = useMemo(() => loadValidQuestionnaireDraft(templateId), [templateId]);
  const initialAnswers = {
    ...(validDraft?.answers || {}),
    ...(defaultValues || {}),
  };
  const initialQuestionIndex = validDraft?.questionIndex;

  const answersRef = useRef<Record<string, unknown>>(initialAnswers);
  const questionIndexRef = useRef<number>(initialQuestionIndex ?? 0);

  const handleStateChange = useCallback(
    (state: { answers: Record<string, unknown>; questionIndex: number }) => {
      answersRef.current = state.answers;
      questionIndexRef.current = state.questionIndex;
    },
    [],
  );

  const handleComplete = useCallback(async (
    meta: { templateId: string; templateVersion: number },
    answers: Record<string, unknown>,
  ) => {
    resetSubmitError();
    updateFlowState('questionnaireMeta', meta);
    updateFlowState('questionnaireAnswers', answers);
    saveQuestionnaireDraft(templateId, {
      answers,
      questionIndex: questionIndexRef.current,
      explicitlySaved: true,
    });

    if (hasBookingConfirmationSnapshot()) {
      const snapshot = loadBookingConfirmationSnapshot();
      if (!snapshot) {
        onNext(answers);
        return;
      }

      const identity = buildIdentityFromBookingSnapshot(snapshot);
      await handleSubmitQuestionnaire(
        identity,
        (completionDisplay) => {
          updateFlowState('identityConfirm', identity);
          updateFlowState('completionDisplay', completionDisplay);
          updateFlowState('submissionResult', { isSuccess: true, errorMessage: '' });
          goToStep(AdhdQuestionnaireFlowStep.COMPLETION);
        },
        { answers, questionnaireMeta: meta },
      );
      return;
    }

    onNext(answers);
  }, [
    goToStep,
    handleSubmitQuestionnaire,
    onNext,
    resetSubmitError,
    templateId,
    updateFlowState,
  ]);

  const isSubmitting = submissionState.status === 'submitting';
  const submitFailure: SubmissionFailure | undefined = submissionState.status === 'error'
    ? submissionState.failure
    : undefined;

  if (!ageRange) {
    return null;
  }

  return (
    <>
      {isSubmitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <Spin size="large" tip="Submitting your answers…" />
        </div>
      )}
      {submitFailure && (
        <div className="fixed inset-x-0 top-4 z-50 px-4 flex justify-center pointer-events-none">
          <div className="max-w-lg w-full pointer-events-auto">
            <SubmitErrorAlert failure={submitFailure} />
          </div>
        </div>
      )}
      <AssessmentQuestionnairePage
        templateId={templateId}
        templateVersion={templateVersion}
        defaultAnswers={initialAnswers}
        initialQuestionIndex={initialQuestionIndex}
        onBack={onBack}
        onAnswersChange={(answers) => {
          answersRef.current = answers;
          // Only auto-save if the user opted in
          const draft = loadValidQuestionnaireDraft(templateId);
          if (draft) {
            saveQuestionnaireDraft(templateId, {
              answers,
              questionIndex: questionIndexRef.current,
              explicitlySaved: true,
            });
          }
        }}
        onStateChange={handleStateChange}
        onNext={handleComplete}
      />
    </>
  );
};

export default AssessmentQuestionnaireStep;
